import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation, useParams } from 'js/lib/useRouter';

import EnterpriseThirdPartyOrganizationContext from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import QueryRosterByOrganizationId from 'bundles/unified-home-common/queries/QueryRostersByOrganizationId.graphql';
import ThirdPartyOrganizationQuery from 'bundles/unified-home-common/queries/ThirdPartyOrganizationQuery.graphql';
import type {
  QueryRosterByOrganizationIdQuery,
  QueryRosterByOrganizationIdQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/QueryRostersByOrganizationId';
import type {
  ThirdPartyOrganizationDataQuery,
  ThirdPartyOrganizationDataQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/ThirdPartyOrganizationQuery';

type Props = {
  children: React.ReactNode;
};

const EnterpriseThirdPartyOrgDataProvider = ({ children }: Props) => {
  const { orgSlug: orgSlugFromParams } = useParams();
  const location = useLocation();
  const orgSlug = orgSlugFromParams || location.query?.orgSlug;

  const {
    data: thirdPartyOrgData,
    loading: isThirdPartyOrgLoading,
    error: thirdPartyOrgError,
  } = useQuery<ThirdPartyOrganizationDataQuery, ThirdPartyOrganizationDataQueryVariables>(ThirdPartyOrganizationQuery, {
    variables: { slug: orgSlug ?? '' },
    context: { clientName: 'gatewayGql' },
    skip: !orgSlug,
  });

  const thirdPartyOrganization =
    thirdPartyOrgData?.NaptimeQueries?.ThirdPartyOrganizationsV1Resource?.bySlug?.elements?.[0] ?? undefined;
  const organizationId = thirdPartyOrganization?.id;

  const { data: rostersData, loading: isOrgContractsLoading } = useQuery<
    QueryRosterByOrganizationIdQuery,
    QueryRosterByOrganizationIdQueryVariables
  >(QueryRosterByOrganizationId, {
    variables: { organizationId: organizationId ?? '' },
    context: { clientName: 'gatewayGql' },
    skip: !organizationId,
  });

  const orgContracts = rostersData?.EnterpriseRosters?.queryRosterByOrganizationId?.elements
    ?.map((roster) => roster.contract)
    .filter(Boolean);

  const hasThirdPartyOrgError = thirdPartyOrgError !== undefined;

  return (
    <EnterpriseThirdPartyOrganizationContext.Provider
      value={{
        orgSlug,
        thirdPartyOrganization,
        hasThirdPartyOrgError,
        isThirdPartyOrgLoading,
        orgContracts,
        isOrgContractsLoading,
      }}
    >
      {children}
    </EnterpriseThirdPartyOrganizationContext.Provider>
  );
};

export default EnterpriseThirdPartyOrgDataProvider;
